import { computed, Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';

/** Persisted per-user localStorage key prefixes for first-visit hints. */
export const FirstVisitHintIds = {
  detailViewActions: 'detail-view-actions-hint-seen',
  menuStructure: 'menu-structure-hint-seen',
  dueListPrintSelection: 'due-list-print-selection-hint-seen',
  medicineStockExpiry: 'medicine-stock-expiry-hint-seen',
  selectItems: 'select-items-hint-seen',
  tableRotation: 'main-table-rotation-hint-seen',
} as const;

export type FirstVisitHintId = (typeof FirstVisitHintIds)[keyof typeof FirstVisitHintIds];

export interface FirstVisitHintConfig {
  /** Lower number = shown earlier when multiple hints are eligible. */
  priority: number;
  /**
   * Which eligible page visit should show the hint.
   * 1 (default) = first visit; 2 = second visit, etc.
   * A visit is counted once per eligibility period (register → unregister).
   */
  showOnVisit?: number;
}

export const FIRST_VISIT_HINT_CONFIG: Record<string, FirstVisitHintConfig> = {
  [FirstVisitHintIds.menuStructure]: { priority: 10 },
  [FirstVisitHintIds.dueListPrintSelection]: { priority: 20, showOnVisit: 2 },
  [FirstVisitHintIds.selectItems]: { priority: 20 },
  [FirstVisitHintIds.detailViewActions]: { priority: 30 },
  [FirstVisitHintIds.medicineStockExpiry]: { priority: 30 },
  [FirstVisitHintIds.tableRotation]: { priority: 40 },
};

@Injectable({
  providedIn: 'root',
})
export class FirstVisitHintService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly store = inject(Store);
  private readonly dismissedIds = signal(new Set<string>());
  private readonly registeredIds = signal(new Set<string>());
  /** Hints that already counted a visit for the current eligibility period. */
  private readonly visitCountedIds = new Set<string>();

  readonly activeHintId = computed(() => {
    const candidates = [...this.registeredIds()]
      .filter((hintId) => this.isUnseen(hintId))
      .sort((left, right) => this.getPriority(left) - this.getPriority(right));

    return candidates[0] ?? null;
  });

  registerHint(hintId: string): void {
    if (!this.isUnseen(hintId)) {
      return;
    }

    this.recordEligibleVisit(hintId);

    if (!this.hasReachedShowVisit(hintId) || this.registeredIds().has(hintId)) {
      return;
    }

    this.registeredIds.update((ids) => {
      const next = new Set(ids);
      next.add(hintId);
      return next;
    });
  }

  unregisterHint(hintId: string): void {
    this.visitCountedIds.delete(hintId);

    if (!this.registeredIds().has(hintId)) {
      return;
    }

    this.registeredIds.update((ids) => {
      const next = new Set(ids);
      next.delete(hintId);
      return next;
    });
  }

  shouldShowHint(hintId: string): boolean {
    return this.activeHintId() === hintId;
  }

  dismissHint(hintId: string): void {
    this.dismissedIds.update((ids) => {
      const next = new Set(ids);
      next.add(hintId);
      return next;
    });
    this.unregisterHint(hintId);

    const storageKey = this.getStorageKey(hintId);
    if (!storageKey) {
      return;
    }
    this.localStorageService.setSavedState(true, storageKey);
  }

  private recordEligibleVisit(hintId: string): void {
    if (this.visitCountedIds.has(hintId)) {
      return;
    }

    this.visitCountedIds.add(hintId);

    const visitKey = this.getVisitCountStorageKey(hintId);
    if (!visitKey) {
      return;
    }

    this.localStorageService.setSavedState(this.getVisitCount(hintId) + 1, visitKey);
  }

  private hasReachedShowVisit(hintId: string): boolean {
    return this.getVisitCount(hintId) >= this.getShowOnVisit(hintId);
  }

  private getVisitCount(hintId: string): number {
    const visitKey = this.getVisitCountStorageKey(hintId);
    if (!visitKey) {
      return 0;
    }

    const saved = this.localStorageService.getSavedState(visitKey);
    return typeof saved === 'number' && saved > 0 ? saved : 0;
  }

  private getShowOnVisit(hintId: string): number {
    return FIRST_VISIT_HINT_CONFIG[hintId]?.showOnVisit ?? 1;
  }

  private getPriority(hintId: string): number {
    return FIRST_VISIT_HINT_CONFIG[hintId]?.priority ?? Number.MAX_SAFE_INTEGER;
  }

  private isUnseen(hintId: string): boolean {
    if (this.dismissedIds().has(hintId)) {
      return false;
    }

    const storageKey = this.getStorageKey(hintId);
    return !!storageKey && this.localStorageService.getSavedState(storageKey) !== true;
  }

  private getStorageKey(hintId: string): string | null {
    const userId = this.getUserId();
    return userId ? `${hintId}_${userId}` : null;
  }

  private getVisitCountStorageKey(hintId: string): string | null {
    const userId = this.getUserId();
    return userId ? `${hintId}_visit-count_${userId}` : null;
  }

  private getUserId(): string | null {
    return this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
  }
}
